import interact from 'interactjs';
import {isEmpty} from 'lodash';
import {uploadFile} from '../api/file';
import {
  COMP_HEIGHT,
  COMP_WIDTH,
  DESIGNBOARD,
  MOUSE_ACTION_COMP_DND,
  MOUSE_ACTION_COMP_INIT,
  MOUSE_ACTION_COMP_RESIZE,
  SNAP_RANGE
} from "../constant/common";
import {astStore} from '../store';
import {
  getASTComponentById,
  updateASTComponentAttr,
  updateASTComponentProp,
  updateASTComponents,
  updateAstComponentsTailwindToCss,
  updateSeletedId
} from "../store/common/action";
import {getValue, IS_TEMPLATE, LEFT_FIELDS} from '../store/common/CacheGlobal';
import {findIn2DArray, generateUUID, getDomStyles, textStyle} from '../utils';
import {ShapeConstants} from '../utils/penpot/constant';
import {calculateBound} from '../utils/penpot/util';
import {
  calculateDragInit,
  calculateResize,
  getBoundingClientRect,
  getRectArr,
  getWidthHeight,
  handleDrag,
  parseToCssDefault,
} from './calcLayout';
import {changeTreeNodeStyle, cleanTree, createSvgElementNS, findFrameByProp, updateTemplate} from './canvasCommon';
import {canvasGlobal} from "./canvasGlobal";

// 组件类，用于缓存状态，避免在捕获时频繁读取dom
class Comp {
  constructor({
                left = 0,
                right = 0,
                top = 0,
                bottom = 0,
                width = COMP_WIDTH,
                height = COMP_HEIGHT,
                selected = false,
                parent = '',
                type,
                compStyles,
                name,
                id,
                css,
                dataKey,
                hidden,
                template = false,
                status = true
              }) {
    this.left = left;
    this.right = right;
    this.top = top;
    this.bottom = bottom;
    this.width = width;
    this.height = height;
    this.selected = selected;
    this.uuid = '';
    this.status = status
    let svg;
    if (type === 'path' && !isEmpty(css)) {
      svg = createSvgElementNS(css);
    }
    const element = document.createElement('div');
    if (status) {
      element.classList.add('comp-template');
      // 不是模板时添加
      if (!template) {
        element.classList.add('comp-instance');
      } else {
        element.classList.add('comp-instanceT');
      }
      element.setAttribute('data-key', dataKey);
      this.uuid = generateUUID();
    } else {
      element.classList.add('comp-instanceT');
      this.uuid = id;
    }
    compStyles = this.repairStyle(compStyles);
    Object.assign(element.style, {
      ...compStyles,
    });
    element.style.width = `${type === 'path' ? calculateBound(css, ShapeConstants.width) : width}px`;
    element.style.height = `${type === 'path' ? calculateBound(css, ShapeConstants.height) : height}px`;
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    if (type === 'circle') {
      element.style.borderRadius = `50%`;
    }

    // pp隐藏web-components跟着隐藏 PP显示web-components跟着显示
    element.style.display = hidden ? 'none' : _get(compStyles, 'display');
    if (type === 'text') {
      element.innerText = name;
    }
    element.setAttribute('data-type', type);
    if (this.selected) {
      element.classList.add('comp-selected');
    }

    this.element = element;
    if (svg) {
      svg.setAttribute('id', this.uuid);
      this.element.appendChild(svg);
    }
    this.element.id = this.uuid;
    this.parent = parent;
    this.parent.appendChild(this.element);
  }



  /**
   * 修正flex、gap等样式属性
   * @param style 原始样式对象
   * @returns 修复后的style对象
   */
  repairStyle(style) {
    const newStyle = { ...style };

    // 1. flexDirection col -> column
    if (newStyle.flexDirection === 'col') {
      newStyle.flexDirection = 'column';
    }

    // 2. rowGap / columnGap 不带单位自动补 px
    const gapKeys = ['rowGap', 'columnGap', 'gap'];
    gapKeys.forEach((key) => {
      const val = newStyle[key];
      if (val !== undefined && val !== null) {
        // 纯数字字符串 例如 "4"、"10"
        if (/^\d+$/.test(String(val))) {
          newStyle[key] = `${val}px`;
        }
      }
    });

    return newStyle;
  }

  /**
   * 改变组件的选中状态
   * @param selected
   */
  toggleSelect(selected) {
    this.selected = selected;
    if (selected) {
      this.element.classList.remove('hovered');
      this.element.classList.add('selected');
      if (!canvasGlobal.componentsSelected.has(this.uuid)) {
        canvasGlobal.componentsSelected.add(this.uuid)
      }
    } else {
      this.element.classList.remove('selected');
    }
  }

  toggleHovered(hovered) {
    //选中状态优先于悬浮状态,当组件处于选中状态时不存在悬浮状态的改变
    if (!this.selected) {
      if (hovered) {
        this.element.classList.add('hovered');
      } else {
        this.element.classList.remove('hovered');
      }
    }
  }

  /**
   * 将拖拽临时状态的组件转换成真实的组件实例
   */
  convertToInstance() {
    this.element.classList.remove('comp-template');
    this.element.classList.remove('comp-instance');
    this.element.classList.add('comp-instanceT');
    const key = this.element.getAttribute('data-key') // 模板key
    const type = this.element.getAttribute('data-type')// 类型
    if (!key) {
      if (['rectangle', 'circle'].includes(type)) {
        this.element.style.background = '#b1b2b5'
      }
      if (['frame', 'group', 'text'].includes(type)) {
        this.element.style.background = 'white'
      }
    }
  }
}

let canvasElm;
let promptTextElm;

// 是否需要从 DOM 内联样式提取到 AST dynamicCss 中
let compsToBeUpdated = [];

/******************************/
// 辅助线相关的全局变量(触发点击事件时，会刷新下面的值，具体赋值，请看刷新函数)
/*****************************/
/**
 * @type {Array} draggables - 存储所有可拖动元素的数组
 * 该数组在初始化时填充，包含页面中所有.draggable类的元素
 */
let draggables = [];

/**
 * @type {HTMLElement|null} activeDraggable - 当前正在拖动的元素
 * 当用户开始拖动一个元素时，该变量被设置为该元素
 * 拖动结束时被重置为null
 */
let activeDraggable = null;

/**
 * @type {number} offsetX - 鼠标相对于元素左上角的水平偏移
 * 当用户点击元素开始拖动时，计算鼠标在元素内部的X坐标
 * 用于在拖动过程中保持鼠标与元素的相对位置不变
 */
let offsetX = 0;

/**
 * @type {number} offsetY - 鼠标相对于元素左上角的垂直偏移
 * 当用户点击元素开始拖动时，计算鼠标在元素内部的Y坐标
 */
let offsetY = 0;

/**
 * @type {number} snapThreshold - 对齐阈值（像素）
 * 当两个元素距离小于这个值时，会触发对齐吸附
 * 这个值越小，对齐越"敏感"，需要更精确的对齐
 * 这个值越大，对齐越"宽松"，更容易触发对齐
 */
let snapThreshold = 10;

// 获取DOM元素引用
let container = document.getElementById(DESIGNBOARD);        // 容器元素，所有可拖动元素的父容器(根节点root面板)
let guideLinesContainer = null; // 辅助线容器，用于动态添加辅助线


export const strParseFromString = (htmlString, selector) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.querySelector(selector);
}

/**
 * 提供给左侧组件添加拖拽事件
 */
export const addLeftComponentEvent = () => {
  // 组件模板的初始化可拖拽
  const templateElm = document.querySelectorAll('.comp-template');
  for (const templateElmElement of templateElm) {
    interact(templateElmElement)
      .on('down', dragTemplateStart)
      .draggable({
        // 设置manualStart，以便拖动克隆t
        manualStart: true,
        modifiers: [
          // 与其他组件绑定
          interact.modifiers.snap({
            targets: [getSnapTarget],
            range: 0,
            offset: 'parent',
            relativePoints: [
              {x: 0, y: 0},
              {x: 0, y: 1},
              {x: 1, y: 0},
              {x: 1, y: 1},
            ],
          }),
        ],
        onstart: dragOrResizeStart,
        onmove: dragTemplateMove,
        onend: dragTemplateEnd,
      });
  }
  /**
   * 从左往右 拖拽
   */
  dragDomTrigger(canvasElm, promptTextElm);
}

/**
 * 提供给中间canvas回显dom
 */
export const canvasEchoWebComponentsDom = () => {
  canvasGlobal.compList = [];
  canvasElm = document.querySelector('#' + DESIGNBOARD);
  promptTextElm = document.querySelector('.prompt-text');
  //  在拖动或调整大小时计算捕捉目标
  echoWebComponentsDom();
  // 观察画布大小，回流组件以使其保持在画布中
  let resizeObserver;
  (function setupCanvasObserver() {
    if (resizeObserver) return; // 防止重复创建
    resizeObserver = new ResizeObserver((entries) => {
      // recurseCalcCompsXY()
      // 在微任务队列中断开观察器，避免阻塞当前事件循环
      queueMicrotask(() => {
        resizeObserver.disconnect();
      });
    });

    resizeObserver.observe(canvasElm);
  })();
}

// 拖拽模板结束
async function dragTemplateEnd(event) {
  console.log('dragTemplateEnd', event);
  const element = event.target;
  const elementRect = element.getBoundingClientRect();
  const canvasRect = canvasElm.getBoundingClientRect();
  const comp = canvasGlobal.compList.find(c => c.element === element);
  if (!comp) {
    element.remove();
    canvasGlobal.compList.splice(canvasGlobal.compList.indexOf(comp), 1);
  }
  // canvas中的If元素
  if (
    elementRect.left > canvasRect.left &&
    elementRect.right < canvasRect.right &&
    elementRect.top > canvasRect.top &&
    elementRect.bottom < canvasRect.bottom
  ) {
    // 转换为comp-instance，避免canvas UI响应
    comp.convertToInstance();
    initCompDraggable(comp);
    initCompResizable(comp);
    await saveComponents(event);
    elementAppendChild(element);
  } else {
    canvasGlobal.compList.splice(canvasGlobal.compList.indexOf(comp), 1);
    element.remove();
  }
  dragOrResizeEnd(event, MOUSE_ACTION_COMP_INIT)
}

/**
 * 添加
 * @param event
 */
export const saveComponents = async (event) => {
  // 类型
  const type = event.target.getAttribute('data-type');
  // prop
  const prop = event.target.getAttribute('id');
  const dataKey = event.target.getAttribute('data-key');
  let list = astStore.state.components;
  if (!list.find(item => item.prop === prop)) {
    let result;
    if (type !== IS_TEMPLATE) {
      result = findIn2DArray(_cloneDeep(await getValue('compAst')), item => item.type === type);
      if (!result) return
      _set(result, 'prop', prop);
      list.push(result);
      updateASTComponents(list)
    } else if (type === IS_TEMPLATE) {
      const tempList = await getValue(LEFT_FIELDS);
      const data = tempList.data['items'].find(item => item._id === dataKey);
      result = data.components[1];
      // 拖拽dom
      const {left, top} = getDomStyles(event.target);
      const {left: parentLeft, top: parentTop} = getCompLeftAndTop(result);
      let compList = _cloneDeep(data['components']);
      mergeCompFun(data)
      for (let i = 0; i < data.components[0].children.length; i++) {
        dfsRecursive(compList, data.components[0].children[0].prop, parseFloat(left), parseFloat(top), parseFloat(left) - parentLeft, parseFloat(top) - parentTop, dataKey);
      }
      mergeCompAst(compList, list, prop);
    }
  }
  updateSeletedId(prop);
};


/**
 * 合并组件小函数
 * @param data
 */
function mergeCompFun(data) {
  const pureFunction = astStore.state.pureFunction
  if (!pureFunction.customFunctionParams) {
    pureFunction.customFunctionParams = []
  }
  if (!pureFunction.customParamsList) {
    pureFunction.customParamsList = []
  }
  if (!pureFunction.maxFunctionList) {
    pureFunction.maxFunctionList = []
  }
  const funcArr = [...pureFunction.customFunctionParams,...data.pureFunction?.customFunctionParams]
  pureFunction.customFunctionParams = uniqueByProp(funcArr, 'prop');
  const filterKeys = new Set(['currentProp', 'currentComponent', 'currentIndex', 'currentItem', 'dataIndex']);
  const paramsList = data.pureFunction?.customParamsList.filter(item => !filterKeys.has(item.key));
  const paramArr = [...pureFunction.customParamsList,...paramsList]
  pureFunction.customParamsList = uniqueByProp(paramArr, 'label');
  if (data.pureFunction?.maxFunctionList && data.pureFunction?.maxFunctionList.length > 0) {
    const maxFuncArr = [...pureFunction.maxFunctionList,...data.pureFunction?.maxFunctionList]
    pureFunction.maxFunctionList = uniqueByProp(maxFuncArr, 'funId');
  }
  astStore.state.pureFunction = pureFunction;
}


/**
 * @param {Array} arr 原数组
 * @param {string} prop 根据哪个属性去重
 */
function uniqueByProp(arr, prop) {
  const map = new Map();
  for (const item of arr) {
    if (!map.has(item[prop])) {
      map.set(item[prop], item);
    }
  }
  return [...map.values()];
}

/**
 * 合并模板组件ast
 * @param compList
 * @param list
 * @param prop
 */
function mergeCompAst(compList, list, prop) {
  const dom = document.getElementById(prop);
  dom.remove();
  compList = compList.filter(item => item.prop !== 'root');
  const updatedArr = list.concat(compList);
  cleanTree(updatedArr);
  let filteredItems = updatedArr.filter(item => item.parent === 'root');
  let rootChildren = [];
  if (filteredItems.length > 0) {
    for (const rootChild of filteredItems) {
      rootChildren.push({prop: rootChild.prop});
    }
  }
  updatedArr[0].children = rootChildren;
  updateASTComponents(updatedArr);
}

function dragDomTrigger(canvasElm, promptTextElm) {
  // 重左往右边拖拽触发
  interact(canvasElm).dropzone({
    // 拖拽compo-instance时不响应
    accept: '.comp-template',
    // 仅当完全在画布中时触发进入
    overlap: 1.0,
    // UI响应激活，进入，离开和停用
    ondropactivate: (event) => {
      event.target.classList.add('canvas-active');
      promptTextElm.style.display = 'block';
      promptTextElm.innerHTML = '拖拽到画布中';
    },
    ondragenter: (event) => {
      event.target.classList.add('canvas-enter');
      promptTextElm.innerHTML = '释放';
    },
    ondragleave: (event) => {
      event.target.classList.remove('canvas-enter');
      promptTextElm.innerHTML = '拖拽到画布中';
    },
    ondropdeactivate: (event) => {
      event.target.classList.remove('canvas-active');
      event.target.classList.remove('canvas-enter');
      promptTextElm.innerHTML = '拖拽';
      promptTextElm.style.display = 'none';
    },
  });
}

// 开始从左往右拖拽前
async function dragTemplateStart(event) {
  compsToBeUpdated = [];
  const interaction = event.interaction;
  // 鼠标按下，还没有开始移动
  if (interaction.pointerIsDown && !interaction.interacting()) {
    // 句柄位置，构造组件对象
    const template = event.target;
    const templateRect = template.getBoundingClientRect();
    const canvasRect = canvasElm.getBoundingClientRect();
    const type = template.getAttribute('data-type');
    let name = ''
    const left = templateRect.left - canvasRect.left;
    const right = templateRect.right - canvasRect.left;
    const top = templateRect.top - canvasRect.top;
    const bottom = templateRect.bottom - canvasRect.top;
    let getCompList = {};
    let dataKey = '';
    let width = 200;
    let height = 50;
    // 处理模板组件
    if (type === IS_TEMPLATE) {
      dataKey = template.getAttribute('data-key');
      const dataList = await getValue(LEFT_FIELDS);
      getCompList = dataList['data']['items'].find(comp => comp._id === dataKey).components[1];
      const size = getCompWidthAndHeight(getCompList);
      width = size.width;
      height = size.height;
    }
    if (type === 'text') {
      name = "请输入文本"
    }
    const comp = new Comp({
      left,
      right,
      top,
      bottom,
      width,
      height,
      selected: false,
      parent: canvasElm,
      type,
      name,
      dataKey,
    });
    if (comp) {
      canvasGlobal.compList.push(comp);
    }
    // 'manualStart'拖拽新组件，新组件接管拖拽事件
    interaction.start({name: 'drag'}, event.interactable, comp.element);
  }
}

// 常用的拖放函数，处理拖放位置，可用于模板和实例
function dragMove(event) {
  compsToBeUpdated = [];
  canvasGlobal.componentsSelected.forEach(prop => {
    moveElement(prop, event.dx, event.dy);
  });
}


/**
 * 组件模板平移（从左侧拖出来时的临时状态）
 * @param event
 */
function dragTemplateMove(event) {
  const target = event.target;
  const x = (parseFloat(target.style.left)) + event.dx;
  const y = (parseFloat(target.style.top)) + event.dy;
  target.style.left = `${x}px`;
  target.style.top = `${y}px`;
}


/**
 * 平移组件及其子节点
 * @param prop
 * @param dx
 * @param dy
 */
function moveElement(prop, dx, dy) {
  let comp = canvasGlobal.compList.find(c => c.element.getAttribute('id') === prop);
  //组件位置是否已经被更新过
  let updated = compsToBeUpdated.find(item => {
    return item === comp
  });
  if (comp && !updated) {
    let {element} = comp;
    const x = (parseFloat(element.style.left)) + dx;
    const y = (parseFloat(element.style.top)) + dy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    compsToBeUpdated.push(comp);
    // 查找子组件
    let components = astStore.state.components;
    let children = components.filter(item => {
      return item.parent === prop;
    });
    children.forEach(child => {
      moveElement(child.prop, dx, dy);
    });
  }
}

// 拖动或调整开始大小，添加选定样式的组件
function dragOrResizeStart(event) {
  //重置本次拖动或调整大小时需要更新的组件列表
  compsToBeUpdated = [];
  canvasGlobal.dragging = true;
  const target = event.target;
  const comp = canvasGlobal.compList.find(c => c.element === target);
  if (comp) return

  if (!comp.selected) {
    //选中当前组件
    chooseComponent([event.target.getAttribute('id')], true);
    //拖拽的是未选中的组件时取消之前的组件选中状态
    canvasGlobal.selection.clearSelection();
    comp.toggleSelect(true);
  }
}

/**
 * @description 整理组件（有祖孙或父子关系的只留祖/父组件）
 * @param { Set<string> } propSet
 * @returns
 */
function filtrateAncestor(propSet) {
  const {components} = astStore.state
  // 往上递归
  const recurseFilter = (prop, set) => {
    const comp = components.find(cp => cp.prop === prop)
    if (!comp) return 0
    const {parent} = comp
    if (set.has(comp.parent)) return 0
    if (parent === 'root') return 1
    return recurseFilter(parent, set) // 沿着父级链往上找
  }
  // 要移动的 prop，只要这些组件移动，则其子级会递归跟着移动
  const reservedProps = []
  propSet.forEach(prop => {
    if (recurseFilter(prop, propSet)) reservedProps.push(prop)
  })
  return reservedProps
}

/**
 *
 * 拖动或调整端部大小，隐藏左辅助行（设计区的组件事件）
 * @param {*} event
 * @param action
 *  MOUSE_ACTION_COMP_DND: 拖拽
 *  MOUSE_ACTION_COMP_RESIZE: 调整大小
 *  MOUSE_ACTION_COMP_INIT: 初始化（两者都没有）
 *
 */
export function dragOrResizeEnd(event, action = MOUSE_ACTION_COMP_DND) {
  const {target: {id}} = event // 无论单选还是多选，event.target 都是当前鼠标选中的那个组件
  let comps = _cloneDeep(astStore.state.components)
  let handleProps = [];
  if (action === MOUSE_ACTION_COMP_DND) { //  移动节点的拖拽，选中一个或多个，并拖拽移动的，
    // 选中多个并拖拽，都生效
    handleProps = filtrateAncestor(canvasGlobal.componentsSelected);
  }
  if (action === MOUSE_ACTION_COMP_RESIZE) {  // 调整节点大小的拖拽
    handleProps = [id]
    calculateResize(id, comps);
  }

  if (action === MOUSE_ACTION_COMP_INIT) {  //  新增节点的拖拽
    handleProps = [id]
    calculateDragInit(id, comps, event);
  }

  // 获取每个 DOM 的 left、top、高、宽
  let rectArr = getRectArr(comps);
  // 处理拖拽后的逻辑
  comps = handleDrag(handleProps, comps, rectArr, event, action);


  updateAstComponentsTailwindToCss(comps)
  // 调用 updateComponents 批量更新
  updateASTComponents(comps)

  // auxLineLeftElm.style.opacity = '0'
  //清空待更新的组件列表
  compsToBeUpdated = []
  canvasGlobal.dragging = false
}

// 调整组件大小，更新状态
function resizeMove(event) {

  let width = `${event.rect.width}px`;
  let height = `${event.rect.height}px`;

  let comps = _cloneDeep(astStore.state.components)

  const target = event.target;
  let prop = target.id;
  let sComp = comps.find(item => item.prop == prop);
  let pComp = comps.find(item => item.prop == sComp.parent);
  let wh = getWidthHeight(pComp)
  // 如果父节点有设置固定高宽，则所有节点调整大小时，都不能超过父节点
  let pDom = document.getElementById(sComp.parent === 'root' ? DESIGNBOARD : sComp.parent);
  let pRect = getBoundingClientRect(pDom);
  if(["w-auto", "w-fit"].indexOf(wh.width) == -1 && (parseFloat(target.style.left) + parseFloat(width)) > pRect.right){
    width = target.style.width
  }
  if(["h-auto", "h-fit"].indexOf(wh.height) == -1 && (parseFloat(target.style.top) + parseFloat(height)) > pRect.bottom){
    height = target.style.height
  }

  // const rx = event.deltaRect.left;
  // const ry = event.deltaRect.top;
  target.style.width = width;
  target.style.height = height;
  // target.style.left = `${parseFloat(target.style.left) + rx}px`;
  // target.style.top = `${parseFloat(target.style.top) + ry}px`;
}

/**
 * 选中/取消选中组件
 * @param { Set<string> | string[] } props 选中/取消选中的组件列表
 * @param bool 选中/取消选中
 * @param isCanvas 是否在右边 Canvas 上操作
 */
export const chooseComponent = (props, bool, isCanvas = true) => {
  let itera = props, isArray = true // 默认为数组类型
  if (props instanceof Set) {
    isArray = false
    itera = props.values()
  }
  for (const prop of itera) {
    const comp = canvasGlobal.compList.find(item => item.uuid === prop)
    comp && comp.toggleSelect(bool) // 设置布局块选中样式
    changeTreeNodeStyle(prop, bool, false, isCanvas) // 设置树取消选中
    if (bool) updateSeletedId(prop) // 刷新右侧配置
  }
  // 若是取消选中，props 都是待取消的，要过滤掉，只留下选中的
  if (!bool) {
    const filteredArray = [...canvasGlobal.componentsSelected].filter(prop => isArray ? !props.includes(prop) : !props.has(prop))
    canvasGlobal.componentsSelected = new Set(filteredArray)
  }
}

// Init组件可拖动
function initCompDraggable(comp) {
  interact(comp.element).draggable({
    enabled: true,
    maxPerElement: 3,
    modifiers: [
      // 与其他组件绑定
      interact.modifiers.snap({
        targets: [getSnapTarget],
        range: SNAP_RANGE,
        offset: 'parent',
        relativePoints: [
          {x: 0, y: 0},
          {x: 0, y: 1},
          {x: 1, y: 0},
          {x: 1, y: 1},
        ],
      }),
      // restrict in canvas
      interact.modifiers.restrictRect({
        restriction: canvasElm,
      }),
    ],
    onstart(event) {   //  鼠标点击组件元素移动时，会执行一次
      dragOrResizeStart(event);
      startDrag(event);    // 辅助线函数，初始化辅助线函数的一些参数，使赋值都是最新的
    },
    onmove(event) {      //  鼠标点击组件元素移动时，会执行
      if (event.ctrlKey) return
      dragMove(event);
      doDrag(event);    // 辅助线函数，组件移动时会判断是否要显示对齐辅助
    },
    onend(event) {  // 鼠标点击组件元素移动后松开时，会执行
      dragOrResizeEnd(event, MOUSE_ACTION_COMP_DND);
      stopDrag();    // 辅助线函数，组件移动结束时会清除对齐辅助
    },
  }).on('doubletap', async function (event) {  // 双击选择元素时会执行
    //双击下钻选择第一个子组件
    let {components} = astStore.state;
    const prop = event.target.getAttribute('id');
    let child = components.find(component => { return component.parent === prop });
    if (child) {
      //取消前一个组件的选中状态
      chooseComponent(canvasGlobal.componentsSelected, false);
      chooseComponent([child.prop], true);
    }
  })
    // tap 事件逻辑与 canvasGlobal.selection 有冲突，注意使用
    /* .on('tap', event => {
      // console.log('tap')
      canvasGlobal.enable = false // 禁用框选
      // do something
      canvasGlobal.enable = true
    }) */
    .on('tap', async function (event) {  // 鼠标点击了元素
      // Canvas 上的点击事件 仅仅在点击的时候调用滚动
      const {target: {id: prop}, ctrlKey} = event
      console.log("prop----",prop)
      canvasGlobal.clickProp = prop
      await customElements.whenDefined('tree-component');
      const treeHTML = document.getElementById('tree-component');
      // 消除树上结点的高亮
      await treeHTML.cancelTreeHighlight()
      await treeHTML.scrollNodeToTop(prop);
    })
    .on('pointerenter', function (event) {  // 鼠标移入组件元素
      const {target: {id: prop}, ctrlKey} = event
      // 组件悬浮
      !canvasGlobal.dragging && enterComponent(prop, ctrlKey)
    })
    .on('pointerleave', function (event) { // 鼠标移出组件元素
      //退出悬浮
      const prop = event.target.getAttribute('id');
      !canvasGlobal.dragging && leaveComponent(prop);
    })

  //注册上下文菜单事件
  comp.element.addEventListener('contextmenu', (e) => {
    e.preventDefault();

    canvasGlobal.mouseX = e.clientX; // 设置坐标的 X 位置，后面右键粘贴需要用到
    canvasGlobal.mouseY = e.clientY; // 设置坐标的 Y 位置，后面右键粘贴需要用到

    chooseComponent(canvasGlobal.componentsSelected, false) // 先取消前一个/多个组件的选中状态
    chooseComponent([e.target.id], true) // 只选中当前组件
    canvasGlobal.contextMenu.display(e);
  })
}

/**
 * 开始拖动处理函数，使赋值都是最新的状态
 * 当用户在可拖动元素上按下鼠标时触发
 * @param {MouseEvent} e - 鼠标事件对象，包含事件相关信息
 *                        e.clientX/Y: 鼠标在视口中的坐标
 *                        e.currentTarget: 事件绑定的元素（当前拖动的元素）
 */
function startDrag(e) {
  // 防止默认行为（如文本选择、图像拖拽等）
  // 这对于拖拽功能很重要，避免浏览器默认行为干扰
  // e.preventDefault();

  // 如果当前拖动的元素为空，就重新设置一下
  if (!container) {
    container = document.getElementById(DESIGNBOARD);        // 容器元素，所有可拖动元素的父容器(根节点root面板)
  }

  // 辅助线的容器，如果第一次未初始化，就创建，并挂到根节点root面板下
  if (!guideLinesContainer) {
    // 出创建div
    guideLinesContainer = document.createElement('div');
    // 设置属性id
    guideLinesContainer.id = 'guideLines';
    // 添加到页面中，挂到根节点root面板下
    container.appendChild(guideLinesContainer);
  }


  // 设置当前拖动的元素
  // e.currentTarget指向绑定事件监听器的元素（.draggable元素）
  activeDraggable = e.currentTarget;

  // 获取元素相对于视口的位置和尺寸
  // getBoundingClientRect()返回一个DOMRect对象，包含元素的位置和大小信息
  const rect = activeDraggable.getBoundingClientRect();

  // 计算鼠标相对于元素左上角的偏移量
  // 这个偏移量在拖动过程中保持不变，确保元素跟随鼠标移动时位置正确
  // clientX - rect.left = 鼠标在元素内的水平位置
  offsetX = e.clientX - rect.left;
  // clientY - rect.top = 鼠标在元素内的垂直位置
  offsetY = e.clientY - rect.top;

  var descendantsArr = getDescendantsArr(activeDraggable.id);  // 获取全部子孙后代节点

  // 先清空，获取最新全部需要对齐的节点，拖动节点的子节点不需要对齐
  draggables = [];
  for (let i = 0; i < canvasGlobal.compList.length; i++) {
    if (descendantsArr.indexOf(canvasGlobal.compList[i].uuid) == -1) {
      draggables.push(canvasGlobal.compList[i].element);
    }
  }

  // 为拖动的元素添加拖动样式，提供视觉反馈
  // .dragging类改变元素的外观（透明度、阴影等）
  // activeDraggable.classList.add('opacity-75');  //  后面需要再开
}

/**
 * 根据id获取所有后代节点
 * @param id
 * @param map
 */
function getDescendantsArr(id, map, descendantsArr) {
  if (typeof descendantsArr === 'undefined') {
    descendantsArr = [];
  }
  if (typeof map === 'undefined') {
    // 获取当前拖动节点 activeDraggable 的后代节点
    map = new Map();
    astStore.state.components.forEach((comp) => {
      map.set(comp.prop, comp);
    });
  }
  var node = map.get(id);
  if (node) {
    node.children.forEach((c) => {
      descendantsArr.push(c.prop);
      getDescendantsArr(c.prop, map, descendantsArr);
    });
  }
  return descendantsArr;
}

/**
 * 停止拖动处理函数
 * 当鼠标释放时触发，结束拖拽操作
 */
function stopDrag() {
  // 安全检查：如果有活动中的拖动元素，进行清理操作
  if (activeDraggable) {
    // 清空活动元素引用
    // 这是重要的一步，确保后续拖动操作正确开始
    activeDraggable = null;
  }

  // 清除所有辅助线
  // 拖拽结束后不再需要显示辅助线
  clearGuideLines();

}

/**
 * 拖动组件时处理辅助线的函数
 * 当鼠标在文档中移动时触发，用于更新被拖动元素的位置
 * @param {MouseEvent} e - 鼠标事件对象
 */
function doDrag(e) {

  // 安全检查：如果没有活动中的拖动元素，直接返回
  // 防止在未开始拖动时意外触发
  if (!activeDraggable) return;

  // 获取容器相对于视口的位置
  // 用于将绝对坐标转换为相对于容器的坐标
  const containerRect = container.getBoundingClientRect();

  // 计算元素的新位置（相对于容器）
  // 1. e.clientX - offsetX: 计算元素的左上角在视口中的X坐标
  // 2. - containerRect.left: 减去容器的偏移量，得到相对于容器的位置
  let newX = e.clientX - offsetX - containerRect.left;
  let newY = e.clientY - offsetY - containerRect.top;

  // 限制元素在容器边界内，防止元素被拖出容器
  // Math.max(0, ...): 确保X坐标不小于0（不超过左边界）
  // Math.min(..., container.clientWidth - activeDraggable.offsetWidth):
  // 确保X坐标不超过右边界（容器宽度 - 元素宽度）
  newX = Math.max(0, Math.min(newX, container.clientWidth - activeDraggable.offsetWidth));
  // 同理限制Y坐标在容器高度范围内
  newY = Math.max(0, Math.min(newY, container.clientHeight - activeDraggable.offsetHeight));

  // 清除之前的辅助线（如果有）
  // 每次拖动时都清除旧的辅助线，重新检测对齐并绘制新的辅助线
  clearGuideLines();

  // // 检查对齐情况，获取调整后的位置和辅助线信息
  // checkAlignment函数检测元素是否与其他元素或容器边界对齐
  // 返回包含调整后坐标和辅助线信息的对象
  const {adjustedX, adjustedY, guides} = checkAlignment(newX, newY, activeDraggable);

  // // 更新元素的位置(拖动组件靠近辅助线一定距离时，会自动吸附上去)
  // // 使用adjustedX和adjustedY而不是原始的newX和newY
  // // 这样当检测到对齐时，元素会自动"吸附"到对齐位置
  // activeDraggable.style.left = `${adjustedX}px`;
  // activeDraggable.style.top = `${adjustedY}px`;


  activeDraggable.style.left = `${newX}px`;
  activeDraggable.style.top = `${newY}px`;

  // 如果有辅助线，显示它们
  // guides数组包含检测到的所有对齐位置信息
  if (guides.length > 0) {
    showGuideLines(guides);          // 在对应位置显示辅助线
  }
}

/**
 * 检查元素是否与其他元素或容器边界对齐
 * 这是对齐功能的核心函数，检测多种对齐情况：
 * 1. 容器边界对齐（上下左右）
 * 2. 元素边缘对齐（左右上下边缘）
 * 3. 元素中心对齐（水平和垂直中心）
 *
 * @param {number} x - 元素当前的X坐标（相对于容器）
 * @param {number} y - 元素当前的Y坐标（相对于容器）
 * @param {HTMLElement} draggingElement - 正在拖动的元素
 * @returns {Object} 包含调整后的坐标和辅助线信息的对象
 *                    - adjustedX: 调整后的X坐标
 *                    - adjustedY: 调整后的Y坐标
 *                    - guides: 辅助线信息数组
 */
function checkAlignment(x, y, draggingElement) {
  // 初始化调整后的坐标（如果没有对齐，就使用原始坐标）
  let adjustedX = x;
  let adjustedY = y;

  // 存储辅助线信息
  // 每个辅助线对象包含类型（vertical/horizontal）和位置
  const guides = [];
  let guidesMap = new Map();  // 用来临时存储 guides，通过key-value处理 最多显示六条线（上、下、左、右、中心-垂直、中心-水平）

  // 计算拖动元素的边界信息
  // 这些信息用于与容器边界和其他元素进行比较
  const draggingRect = {
    left: x,  // 元素左边界X坐标
    right: x + draggingElement.offsetWidth,  // 元素右边界X坐标
    top: y,  // 元素上边界Y坐标
    bottom: y + draggingElement.offsetHeight,  // 元素下边界Y坐标
    centerX: x + draggingElement.offsetWidth / 2,  // 元素水平中心X坐标
    centerY: y + draggingElement.offsetHeight / 2   // 元素垂直中心Y坐标
  };

  // ============================================
  // 检查容器边界对齐
  // 检测元素是否靠近容器的四个边界
  // ============================================

  // 检查左边界对齐：元素左边界是否靠近容器左边界（0）
  if (Math.abs(draggingRect.left) < snapThreshold) {
    adjustedX = 0;  // 吸附到左边界（X坐标为0）
    let direction = "edge-left-left";
    guidesMap.set(direction, {type: 'vertical', position: container.clientWidth, direction: direction});
  }

  // 检查右边界对齐：元素左边界是否靠近容器右边界
  // 注意：容器右边界的位置是容器宽度减去元素宽度
  const rightBoundary = container.clientWidth - draggingElement.offsetWidth;
  if (Math.abs(draggingRect.left - rightBoundary) < snapThreshold) {
    adjustedX = rightBoundary;  // 吸附到右边界
    let direction = "edge-left-right";
    guidesMap.set(direction, {type: 'vertical', position: container.clientWidth, direction: direction});
  }

  // 检查上边界对齐：元素上边界是否靠近容器上边界（0）
  if (Math.abs(draggingRect.top) < snapThreshold) {
    adjustedY = 0;  // 吸附到上边界（Y坐标为0）
    let direction = "edge-top-top";
    guidesMap.set(direction, {type: 'horizontal', position: 0, direction: direction});
  }

  // 检查下边界对齐：元素上边界是否靠近容器下边界
  // 注意：容器下边界的位置是容器高度减去元素高度
  const bottomBoundary = container.clientHeight - draggingElement.offsetHeight;
  if (Math.abs(draggingRect.top - bottomBoundary) < snapThreshold) {
    adjustedY = bottomBoundary;  // 吸附到下边界
    let direction = "edge-top-bottom";
    guidesMap.set(direction, {type: 'horizontal', position: container.clientHeight, direction: direction});
  }

  // ============================================
  // 检查与其他元素的对齐
  // 遍历所有其他元素，检测多种对齐情况
  // ============================================

  // 遍历所有可拖动元素（除了当前拖动的元素）， 子节点的不需要
  draggables.forEach(otherElement => {
    // 跳过自身比较
    if (otherElement === draggingElement) return;

    // 获取其他元素相对于容器的位置
    // 注意：其他元素可能也在移动，所以每次都要重新获取位置
    const otherRect = otherElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // 计算其他元素相对于容器的坐标
    const otherX = otherRect.left - containerRect.left;
    const otherY = otherRect.top - containerRect.top;

    // 计算其他元素的边界信息（与拖动元素相同的结构）
    const other = {
      left: otherX,  // 其他元素左边界
      right: otherX + otherElement.offsetWidth,  // 其他元素右边界
      top: otherY,  // 其他元素上边界
      bottom: otherY + otherElement.offsetHeight,  // 其他元素下边界
      centerX: otherX + otherElement.offsetWidth / 2,  // 其他元素水平中心
      centerY: otherY + otherElement.offsetHeight / 2   // 其他元素垂直中心
    };

    // ============================================
    // 边缘对齐检测
    // 检测拖动元素与其他元素的边缘对齐
    // ============================================

    // 检查左边缘对齐（拖动元素的左边缘与其他元素的左边缘对齐）
    // 即两个元素的左侧在一条垂直线上
    if (Math.abs(draggingRect.left - other.left) < snapThreshold) {
      let direction = "border-left";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.left - other.left) < Math.abs(draggingRect.left - guidesMap.get(direction).position) ){// 如果附近辅助线有多条，就取最近的一条
        adjustedX = other.left;  // 吸附到其他元素的左边缘
        guidesMap.set(direction, {type: 'vertical', position: other.left, direction: direction});
      }
    }

    // 检查左边缘对齐（拖动元素的左边缘与其他元素的右边缘对齐）
    // 即两个元素的左侧在一条垂直线上
    if (Math.abs(draggingRect.left - other.right) < snapThreshold) {
      let direction = "border-left";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.left - other.right) < Math.abs(draggingRect.left - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedX = other.right;  // 吸附到其他元素的左边缘
        guidesMap.set(direction, {type: 'vertical', position: other.right, direction: direction});
      }
    }

    // 检查右边缘对齐（拖动元素的右边缘与其他元素的右边缘对齐）
    // 即两个元素的右侧在一条垂直线上
    if (Math.abs(draggingRect.right - other.right) < snapThreshold) {
      // 调整X坐标使右边缘对齐
      // 注意：要使右边缘对齐，需要设置左边界 = 其他元素右边界 - 拖动元素宽度
      let direction = "border-right";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.right - other.right) < Math.abs(draggingRect.right - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedX = other.right - draggingElement.offsetWidth;
        guidesMap.set(direction, {type: 'vertical', position: other.right, direction: direction});
      }
    }

    // 检查右边缘对齐（拖动元素的右边缘与其他元素的左边缘对齐）
    // 即两个元素的右侧在一条垂直线上
    if (Math.abs(draggingRect.right - other.left) < snapThreshold) {
      // 调整X坐标使右边缘对齐
      // 注意：要使右边缘对齐，需要设置左边界 = 其他元素右边界 - 拖动元素宽度
      let direction = "border-right";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.right - other.left) < Math.abs(draggingRect.right - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedX = other.left - draggingElement.offsetWidth;
        guidesMap.set(direction, {type: 'vertical', position: other.left, direction: direction});
      }
    }

    // 检查上边缘对齐（拖动元素的上边缘与其他元素的上边缘对齐）
    // 即两个元素的上侧在一条水平线上
    if (Math.abs(draggingRect.top - other.top) < snapThreshold) {
      let direction = "border-top";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.top - other.top) < Math.abs(draggingRect.top - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedY = other.top;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'horizontal', position: other.top, direction: direction});
      }
    }

    // 检查上边缘对齐（拖动元素的上边缘与其他元素的下边缘对齐）
    // 即两个元素的上侧在一条水平线上
    if (Math.abs(draggingRect.top - other.bottom) < snapThreshold) {
      let direction = "border-top";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.top - other.bottom) < Math.abs(draggingRect.top - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedY = other.bottom;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'horizontal', position: other.bottom, direction: direction});
      }
    }

    // 检查下边缘对齐（拖动元素的下边缘与其他元素的下边缘对齐）
    // 即两个元素的下侧在一条水平线上
    if (Math.abs(draggingRect.bottom - other.bottom) < snapThreshold) {
      // 调整Y坐标使下边缘对齐
      // 注意：要使下边缘对齐，需要设置上边界 = 其他元素下边界 - 拖动元素高度
      let direction = "border-bottom";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.bottom - other.bottom) < Math.abs(draggingRect.bottom - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedY = other.bottom - draggingElement.offsetHeight;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'horizontal', position: other.bottom, direction: direction});
      }
    }

    // 检查下边缘对齐（拖动元素的下边缘与其他元素的上边缘对齐）
    // 即两个元素的下侧在一条水平线上
    if (Math.abs(draggingRect.bottom - other.top) < snapThreshold) {
      // 调整Y坐标使下边缘对齐
      // 注意：要使下边缘对齐，需要设置上边界 = 其他元素下边界 - 拖动元素高度
      let direction = "border-bottom";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.bottom - other.top) < Math.abs(draggingRect.bottom - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedY = other.top - draggingElement.offsetHeight;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'horizontal', position: other.top, direction: direction});
      }
    }

    // ============================================
    // 中心对齐检测
    // 检测拖动元素与其他元素的中心对齐
    // ============================================

    // 检查水平中心对齐（拖动元素的水平中心与其他元素的水平中心对齐）
    // 即两个元素的水平中心在一条垂直线上
    if (Math.abs(draggingRect.centerX - other.centerX) < snapThreshold) {
      // 调整X坐标使中心对齐
      // 要使中心对齐，需要设置左边界 = 其他元素中心X - 拖动元素宽度的一半
      let direction = "centerX";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.centerX - other.centerX) < Math.abs(draggingRect.centerX - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedX = other.centerX - draggingElement.offsetWidth / 2;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'vertical', position: other.centerX, direction: direction});
      }
    }

    // 检查垂直中心对齐（拖动元素的垂直中心与其他元素的垂直中心对齐）
    // 即两个元素的垂直中心在一条水平线上
    if (Math.abs(draggingRect.centerY - other.centerY) < snapThreshold) {
      // 调整Y坐标使中心对齐
      // 要使中心对齐，需要设置上边界 = 其他元素中心Y - 拖动元素高度的一半
      let direction = "centerY";
      if (guidesMap.get(direction) == null || Math.abs(draggingRect.centerY - other.centerY) < Math.abs(draggingRect.centerY - guidesMap.get(direction).position)){// 如果附近辅助线有多条，就取最近的一条
        adjustedY = other.centerY - draggingElement.offsetHeight / 2;  // 吸附到其他元素的上边缘
        guidesMap.set(direction, {type: 'horizontal', position: other.centerY, direction: direction});
      }
    }
  });

  // 遍历键值对, 将 guidesMap 的数组存储到数组 guides 中
  for (const [key, value] of guidesMap) {
    guides.push(value);
  }

  // 返回调整后的坐标和辅助线信息
  // 如果没有检测到对齐，adjustedX/Y保持原始值，guides为空数组
  return {adjustedX, adjustedY, guides};
}

/**
 * 显示辅助线
 * 根据对齐检测结果，在对应位置创建并显示辅助线
 * @param {Array} guides - 辅助线信息数组
 *                        每个对象包含：
 *                        - type: 'vertical' 或 'horizontal'
 *                        - position: 辅助线的位置（像素）
 */
function showGuideLines(guides) {
  // 遍历所有辅助线信息
  guides.forEach(guide => {
    // 创建辅助线元素
    const guideLine = document.createElement('div');

    // 设置辅助线基础CSS样式
    guideLine.style.position = "absolute";  //绝对定位，相对于容器
    guideLine.style.backgroundColor = "rgba(59, 130, 246, 0.7)";  // 半透明蓝色，rgba中a=0.7表示70%透明度
    guideLine.style.zIndex = 100;   //层叠顺序，确保辅助线在最上层显示
    guideLine.style.pointerEvents = "none"; //禁止鼠标事件，防止辅助线干扰拖拽操作

    if (guide.type == "vertical") {         // 垂直辅助线样式
      guideLine.style.width = "2px";      // 线宽为2像素
      guideLine.style.height = "100%";    // 高度充满整个容器
    } else if (guide.type == "horizontal") { // 水平辅助线样式
      guideLine.style.width = "100%";     // 宽度充满整个容器
      guideLine.style.height = "2px";     //线高为2像素
    }

    // 根据辅助线类型设置位置和样式
    if (guide.type === 'vertical') {
      // 垂直辅助线：设置left位置，高度占满容器
      guideLine.style.left = `${guide.position}px`;
      guideLine.style.top = '0';
    } else {
      // 水平辅助线：设置top位置，宽度占满容器
      guideLine.style.top = `${guide.position}px`;
      guideLine.style.left = '0';

      // 为水平辅助线添加虚线样式
      // 使用border-top而不是background-color来创建虚线效果
      guideLine.style.borderTop = '2px dashed rgba(59, 130, 246, 0.7)';
      guideLine.style.height = '0';  // 设置为0，使用border-top显示线条
    }

    // 将辅助线添加到容器中
    guideLinesContainer.appendChild(guideLine);
  });
}

/**
 * 清除所有辅助线
 * 每次拖动开始时或拖动结束后调用，清空辅助线容器
 */
function clearGuideLines() {
  // 清空辅助线容器的内容
  // innerHTML = '' 移除所有子元素
  guideLinesContainer.innerHTML = '';
}


function getSnapTarget() {
  return {};
}

// 组件调整大小
function initCompResizable(comp) {
  interact(comp.element).resizable({
    enabled: true,
    edges: {left: true, right: true, top: true, bottom: true},
    margin: 5,
    modifiers: [
      // snap to other components
      interact.modifiers.snapEdges({
        targets: [getSnapTarget],
        range: SNAP_RANGE,
        offset: 'parent',
        relativePoints: [
          {x: 0, y: 0},
          {x: 0, y: 1},
          {x: 1, y: 0},
          {x: 1, y: 1},
        ],
      }),
      //限制最小尺寸
      interact.modifiers.restrictSize({
        min: {width: COMP_WIDTH, height: COMP_HEIGHT},
      }),
      //在画布中限制
      interact.modifiers.restrictEdges({
        outer: canvasElm,
      }),
    ],
    onstart(event) {
      dragOrResizeStart(event);
    },
    onmove(event) {
      resizeMove(event)
    },
    onend(event) {
      dragOrResizeEnd(event, MOUSE_ACTION_COMP_RESIZE)
    },
  });
}

/**
 * 组件悬浮（树悬浮时调用此方法）
 * @param prop
 * @param isPressingCtrl
 */
export const enterComponent = (prop, isPressingCtrl = false, isCanvas = true) => {
  let comp = canvasGlobal.compList.find(item => { return item.uuid === prop });
  if (comp && !comp.selected) {
    comp.toggleHovered(!isPressingCtrl)
    changeTreeNodeStyle(prop, !isPressingCtrl, !isPressingCtrl, isCanvas)
  }
}

/**
 * 组件退出悬浮（树退出悬浮时调用此方法）
 * @param prop
 */
export const leaveComponent = (prop, isCanvas = true) => {
  let comp = canvasGlobal.compList.find(item => { return item.uuid === prop });
  if (comp && !comp.selected) {
    comp.toggleHovered(false);
    changeTreeNodeStyle(prop, false, false, isCanvas);
  }
}

/**
 * 递归生成模板组件
 * @param dataList
 * @param prop
 * @param x
 * @param y
 * @param pX
 * @param pY
 * @param dataKey
 * @param parentHidden
 */
function dfsRecursive(dataList, prop, x, y, pX, pY, dataKey, parentHidden = false, isChild = false) {
  const compObj = dataList.find(item => item.prop === prop);
  if (!compObj) {
    return;
  }
  const hidden = parentHidden || _get(compObj.css, ShapeConstants.hidden);
  const type = compObj.type;
  let name = '';

  // 获取坐标
  let {left: originLeft, top: originTop} = getCompLeftAndTop(compObj);

  // 获取宽高
  const {width, height} = getCompWidthAndHeight(compObj)
  // 提取组件样式
  let compStyles = {};
  compStyles = parseToCssDefault(compObj);

  // path类型要去除背景色，否则会覆盖svg.
  if (type === 'path') {
    delete compStyles.backgroundColor;
  }

  if (compObj.type === 'text') {
    compStyles = textStyle(compStyles, height);
  }
  if (type === 'text') {
    name = _get(compObj, ["attrs", "textValue", "value"]);
  }
  let css = {}
  if (compObj.type === 'path') {
    css = _get(compObj, ['attrs', 'pathCss', 'value']) || compObj.css
  }

  const comp = new Comp({
    originLeft,
    originTop,
    width,
    height,
    selected: false,
    parent: canvasElm,
    type,
    name,
    compStyles,
    css,
    hidden,
    dataKey,
    template: true,
  });
  // 替换模板组件里面的prop
  const {uuid} = comp;
  updateTemplate(dataList, compObj, uuid, x ?? originLeft, y ?? originTop, isChild);
  if (comp) {
    canvasGlobal.compList.push(comp);
  }
  initCompDraggable(comp);
  initCompResizable(comp);
  comp.convertToInstance();
  elementAppendChild(comp.element)
  // 遍历每个子节点
  if (compObj.children && compObj.children.length > 0) {
    for (const child in compObj.children) {
      const cProp = compObj.children[child].prop;
      dfsRecursive(dataList, cProp, null, null, originLeft, originTop, dataKey, hidden, true);
    }
  }
}

/**
 * 更新新增的ast到设计态画板.
 *
 * @param components - 新增的ast列表.
 * @param topProp - 顶级节点的prop.
 */
export const updateAtomsToDom = (components, topProp) => {
  const documentFragment = document.createDocumentFragment();

  // 遍历当前及后代生成DOM.
  treeEchoDom(components, topProp, documentFragment);

  // 将新原子组件们插入画板DOM.
  canvasElm.appendChild(documentFragment);

  // 遍历当前及后代修正坐标.
  // recurseCalcCompsXY(topProp,false,false);

  // 初始化新插入的原子组件DOM的拖动及缩放功能.
  components.forEach(component => {
    const comp = canvasGlobal.compList.find(comp => comp.uuid === component.prop);

    if (comp) {
      initCompDraggable(comp);
      initCompResizable(comp);
    }
  });
};

/**
 * 回显
 *
 */
export const echoWebComponentsDom = () => {
  const dataValue = astStore.state;
  const data = dataValue['components'];
  // 增加虚拟dom，一次操作完成
  let oTemp = document.createDocumentFragment();
  if (data[0].children.length > 0) {
    for (const parent of data[0].children) {
      treeEchoDom(data, parent.prop, oTemp);
    }
  }
  canvasElm.appendChild(oTemp);
  /**
   * 兼容组合页 存在key dom不赋予拖拽
   */
  for (const comp in canvasGlobal.compList) {
    initCompDraggable(canvasGlobal.compList[comp]);
    initCompResizable(canvasGlobal.compList[comp]);
  }
};

/**
 * 根据树结构递归生成dom原子组件
 * @param tree
 * @param prop
 * @param oTemp
 * @param parentHidden
 */
export const treeEchoDom = (tree, prop, oTemp, parentHidden = false) => {
  // 查找当前节点
  const compObj = findFrameByProp(tree, prop) || {};

  const hidden = parentHidden || false;
  // 提取位置信息（px）
  const {left, top} = getCompLeftAndTop(compObj);

  // 提取宽高（px）
  const {width, height} = getCompWidthAndHeight(compObj)
  // 提取其他信息
  const id = compObj.prop;
  const type = compObj.type;
  let name = '';
  // 直接取textValue的值作为展示值
  if (type === 'text') {
    name =_get(compObj, ['attrs','textValue','value']);
  }

  // 提取组件样式
  let compStyles = {};
  // 动态样式转换成标准样式
  compStyles = parseToCssDefault(compObj);

  // path类型要去除背景色，否则会覆盖svg.
  if (type === 'path') {
    delete compStyles.backgroundColor;
  }

  if (compObj.type === 'text') {
    compStyles = textStyle(compStyles, height);
  }
  let pathCss = {}
  if (compObj.type === 'path') {
    pathCss = _get(compObj, ['attrs', 'pathCss', 'value'])
  }
  // 生成组件列表
  generateComponentsList(width, height, left, top, id, type, name, compStyles, pathCss, oTemp, hidden);

  // 递归处理子节点
  if (compObj.children && compObj.children.length > 0) {
    for (let i = 0; i < compObj.children.length; i++) {
      const childProp = compObj.children[i].prop;
      treeEchoDom(tree, childProp, oTemp, hidden);
    }
  }
};


// 循环生成组件
export const generateComponentsList = (width, height, left, top, id, type, name, compStyles, css, oTemp, hidden) => {
  const comp = new Comp({
    left,
    top,
    width,
    height,
    selected: false,
    parent: oTemp,
    id,
    type,
    name,
    compStyles,
    css,

    hidden,
    status: false
  });
  if (comp) {
    elementAppendChild(comp.element)
    canvasGlobal.compList.push(comp);
  }
};

/**
 * 获取组件坐标
 * @param compObj
 * @returns {{left, top}}
 */
export const getCompLeftAndTop = (compObj) => {
  const styles = parseToCssDefault(compObj);
  let left = _get(styles, 'left', 0);
  let top = _get(styles, 'top', 0);
  // const rem = parseFloat(getRemValue());
  //
  // const leftVal = parseTailwindValueDesign('left', left) * rem; // * rem，转成 px
  // const topVal = parseTailwindValueDesign('top', top) * rem;

  return {left, top};
}

/**
 * 获取组件宽高
 * @param compObj
 * @returns {{width, height}}
 */
export const getCompWidthAndHeight = (compObj) => {
  const styles = parseToCssDefault(compObj);
  let width = _get(styles, 'width', 0);
  let height = _get(styles, 'height', 0);
  if (['full'].includes(width)){
    width = 0
  }
  if (['full'].includes(height)){
    height = 0
  }
  // const rem = parseFloat(getRemValue());
  // const widthVal = parseTailwindValueDesign('width', width) * rem; // * rem，转成 px
  // const heightVal = parseTailwindValueDesign('height', height) * rem;

  return {width, height};
}


/**
 * 组件拖入设计画布后，向 comp-instanceT 内追加预览内容。
 *
 * - 简单组件（frame/group/text）：无需调用，comp-instanceT 的内联样式即完整预览
 * - 原生标签（iframe/video/audio）：直接插入 HTML 元素，层级较浅
 * - 复杂组件（canvas/markdown 等）：插入 Stencil 设计态组件，需独立生命周期管理
 *
 * @param element comp-instanceT 节点
 */
export const elementAppendChild = (element) => {
  const dataType = element.getAttribute('data-type');
  const pluginName = PLUGIN_MAP[dataType];
  if (!pluginName) return;

  const id = element.getAttribute('id');
  const componentAst = getASTComponentById(id);

  const handler = PLUGIN_RENDERERS[pluginName] || appendStencilComponent;
  handler(element, componentAst, pluginName, id);
};


/**
 * pluginName -> 渲染函数 映射；未命中时走 appendStencilComponent
 */
const PLUGIN_RENDERERS = {
  audio: (element) => appendAudio(element),
  video: (element) => appendVideo(element),
  iframe: (element, componentAst) => appendIframe(element, componentAst),
  input: (element, componentAst) => appendInput(element, componentAst),
  icon: (element, componentAst) => appendIcon(element, componentAst),
  image: (element) => appendImageUploader(element),
};

/**
 * data-type -> 自定义元素标签名 映射
 */
const PLUGIN_MAP = {
  table: 'grid-manager-table',
  'data-selection': 'input',
  video: 'video',
  audio: 'audio',
  input: 'input',
  rbac: 'input',
  date: 'design-date-picker',
  calendar: 'calendar-component',
  tree: 'plug-xn-tree',
  'filter-generator': 'filter-generator',
  selectChoices: 'design-select-choices',
  dhtmlxGantt: 'dhtmlxGantt',
  iframe: 'iframe',
  richText: 'design-md-rich-text',
  upload: 'design-dropzone-upload',
  markdown: 'design-custom-markdown',
  recorder: 'design-custom-atom-recorder',
  'three-canvas': 'design-three-canvas',
  icon: 'icon',
  image: 'image',
  // canvas 需 Fabric.js 在设计态编辑 canvas_ast，运行态 custom-atom-canvas 只读展示
  canvas: 'design-custom-atom-canvas',
};


/** audio 预览 */
const appendAudio = (element) => {
  const htmlString = `<audio style="width: 100%;height: 100%" src="" controls autoplay loop muted preload="auto"></audio>`;
  const audio = strParseFromString(htmlString, 'audio');
  audio.style.pointerEvents = 'none';
  element.appendChild(audio);
};

/** video 预览 */
const appendVideo = (element) => {
  const htmlString = `<video src="" poster="" preload="metadata" controls="" style="width: 100%; height: 100%;"></video>`;
  const video = strParseFromString(htmlString, 'video');
  video.style.pointerEvents = 'none';
  element.appendChild(video);
};

/** iframe 预览 */
const appendIframe = (element, componentAst) => {
  const htmlString = `<iframe style="width: 100%; height: 100%; border: 0;" src=${_get(componentAst, 'attrs.url.value')}
            title=${_get(componentAst, 'attrs.title.value')};></iframe>`;
  const iframe = strParseFromString(htmlString, 'iframe');
  iframe.style.pointerEvents = 'none';
  element.appendChild(iframe);
};

/** input / data-selection / rbac 预览 */
const appendInput = (element, componentAst) => {
  const hasInlineBorder = element.style.border !== '';
  const inputType = _get(componentAst, 'attrs.inputType.value');
  const htmlString = `<input type="${inputType}" style="width: 100%;height: 100%;border-radius: 5px;display: block" value=${_get(componentAst, 'attrs.textValue.value', '')}>`;
  const input = strParseFromString(htmlString, 'input');
  input.style.pointerEvents = 'none';

  if (!hasInlineBorder && inputType !== 'radio') {
    element.style.border = '1px solid rgb(209, 213, 219)';
  }
  if (hasInlineBorder) {
    input.style.webkitAppearance = 'none';
    input.style.mozAppearance = 'none';
    input.style.appearance = 'none';
    input.style.border = 'none';
    input.style.outline = 'none';
  }
  element.appendChild(input);
};

/** icon 预览 */
const appendIcon = (element, componentAst) => {
  const iconName = _get(componentAst, ['attrs', 'textValue', 'value']);
  const titleVal = _get(componentAst, 'attrs.title.value');
  const icon = document.createElement('div');
  icon.classList.add('fa-solid');
  if (iconName) {
    icon.classList.add(`fa-${iconName}`);
  }
  if (titleVal) {
    icon.setAttribute('title', titleVal);
  }
  element.appendChild(icon);
};

/** image 预览（透明上传 input，禁止点击） */
const appendImageUploader = (element) => {
  // 防止重复挂载
  if (element.querySelector('input[type="file"]')) return;

  const inputFile = document.createElement('input');
  inputFile.setAttribute('type', 'file');
  inputFile.setAttribute('multiple', 'false');
  inputFile.setAttribute('accept', 'image/*');
  inputFile.style.setProperty('position', 'absolute');
  inputFile.style.setProperty('width', '60%');
  inputFile.style.setProperty('height', '60%');
  inputFile.style.setProperty('top', '50%');
  inputFile.style.setProperty('left', '50%');
  inputFile.style.setProperty('transform', 'translate(-50%, -50%)');
  inputFile.style.setProperty('opacity', '0');
  // 禁止点击
  inputFile.style.setProperty('pointer-events', 'none');
  inputFile.style.setProperty('cursor', 'default');

  inputFile.addEventListener('change', async (event) => {
    const file = _get(event, 'target.files.0');
    if (!file) return;

    const prop = element.getAttribute('id');
    const data = astStore.state.components.find(component => component.prop === prop);
    if (!data) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await uploadFile(formData);
      const url = _get(res, 'data.item.url');
      if (url) {
        updateASTComponentProp(data, 'dynamicCss.0.sources.0.css.normal.background-image', `bg-[url(${url})]`);
        updateASTComponentAttr(data, 'url', url);
      }
    } catch (e) {
      console.log(e);
    }
  });

  element.appendChild(inputFile);
};

/** 复杂 Stencil 设计态组件（canvas / markdown / tree 等） */
const appendStencilComponent = (element, componentAst, pluginName, id) => {
  const tableElement = document.createElement(pluginName);
  if (['plug-xn-tree', 'design-select-choices'].includes(pluginName)) {
    // 传递高进去，里面要接收
    tableElement.setAttribute('tree-height', `${element.style.height}`);
  }
  // 关联 AST 组件 id，供预览组件读取 attrs
  tableElement.setAttribute('data-design-comp-id', id);
  // canvas 设计态需接收鼠标事件以编辑 Fabric 内容，其余插件仍由 comp-instanceT 接管交互
  tableElement.style.pointerEvents = pluginName === 'design-custom-atom-canvas' ? 'auto' : 'none';
  element.appendChild(tableElement);
};
